import React, { useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';

const ResetPasswordPage = () => {
  const { branding } = useBranding();
  const appName = branding?.app_name || 'BOM Maestro';
  const brandLogo = branding?.logo_data || '';
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (pw !== pw2) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/login'), 2200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Reset failed. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md industrial-card">
        <CardHeader className="border-b border-slate-100 pb-4 space-y-3">
          {brandLogo && (
            <div className="flex items-center justify-center bg-white rounded-md py-3 px-4 border border-slate-200 mx-auto">
              <img src={brandLogo} alt={appName} className="h-10 max-w-[180px] object-contain" data-testid="reset-brand-logo" />
            </div>
          )}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 text-orange-600 mb-2">
              <KeyRound className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">{params.get('invite') ? 'Set your password' : 'Reset your password'}</h1>
            <p className="text-sm text-slate-500 mt-1">{params.get('invite') ? 'Welcome! Choose a password to finish setting up your account.' : 'Choose a new password for your account.'}</p>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          {!token ? (
            <p className="text-sm text-red-600 text-center" data-testid="reset-no-token">
              Reset link is missing. Please <Link to="/forgot-password" className="underline text-orange-600">request a new one</Link>.
            </p>
          ) : done ? (
            <div className="text-center space-y-3 py-3" data-testid="reset-success">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-700">Password reset! Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rp-pw">New password</Label>
                <Input
                  id="rp-pw"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  autoFocus
                  minLength={8}
                  className="rounded-sm"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-pw2">Confirm new password</Label>
                <Input
                  id="rp-pw2"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  minLength={8}
                  className="rounded-sm"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {error && (
                <p className="text-xs text-red-600" data-testid="reset-password-error">{error}</p>
              )}
              <Button
                type="submit"
                disabled={loading || !pw || !pw2}
                className="w-full bg-orange-500 hover:bg-orange-600 rounded-sm"
                data-testid="reset-password-submit-btn"
              >
                {loading ? 'Resetting…' : 'Set new password'}
              </Button>
            </form>
          )}
          <div className="text-center mt-4">
            <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900" data-testid="reset-password-back-link">Back to sign in</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
